/**
 * CollaborationManager Component
 * Manages document collaboration within DMSManager
 * Displays collaborators, handles invitations, and manages permissions
 */

import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { io } from 'socket.io-client';
import { useDocumentCheckoutStore } from '../stores/useDocumentCheckoutStore';
import {
  UserPlusIcon,
  CogIcon,
  EyeIcon,
  PencilIcon,
  ChatBubbleLeftIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  LockClosedIcon,
  LockOpenIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { CollaborationUser } from '../../edgedocs/types/Collaboration.types';
import { useDocumentCollaboration } from '../stores/useDocumentCollaborationStore';
import { UserAvatar, UserAvatarGroup } from './UserAvatar';
import InviteUserModal from './InviteUserModal';
import { CollaborationSettingsDrawer } from './CollaborationSettingsDrawer';
import { RemoveCollaboratorModal } from './RemoveCollaboratorModal';
import { useWebSocketCheckout } from '../hooks/useWebSocketCheckout';
import { CheckinModal } from './CheckinModal';
import { CheckoutTimer, CheckoutTimerBadge } from './CheckoutTimer';

export interface CollaborationManagerProps {
  documentId: string;
  versionId: string;
  isCurrentVersion: boolean;
  ownerPermissions: boolean;
  // Removed collaborators prop - component now loads its own version-specific data
  className?: string;
  onManagePermissions?: (userId: string) => void;
  documentTitle?: string;
  currentVersion?: string;
  refreshTrigger?: number; // Add refresh trigger prop
  onDocumentUpdate?: () => void; // Callback to refresh document data after checkin
  onVersionsRefresh?: () => void; // Callback to refresh versions list
}

interface CollaborationStatsDisplayProps {
  stats: {
    totalCollaborators: number;
    activeCollaborators: number;
    unreadComments: number;
    pendingChanges: number;
    lastActivity?: Date;
  };
  compact?: boolean;
}

// Stats display component
const CollaborationStatsDisplay: React.FC<CollaborationStatsDisplayProps> = ({ 
  stats, 
  compact = false 
}) => {
  if (compact) {
    return (
      <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>{stats.activeCollaborators} active</span>
        </div>
        {stats.unreadComments > 0 && (
          <div className="flex items-center space-x-1">
            <ChatBubbleLeftIcon className="w-3 h-3" />
            <span>{stats.unreadComments} comments</span>
          </div>
        )}
        {stats.pendingChanges > 0 && (
          <div className="flex items-center space-x-1">
            <PencilIcon className="w-3 h-3" />
            <span>{stats.pendingChanges} changes</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div className="flex items-center space-x-2">
        <UserGroupIcon className="w-4 h-4 text-lex-accent" />
        <span className="text-gray-700 dark:text-gray-300">
          {stats.totalCollaborators} collaborator{stats.totalCollaborators !== 1 ? 's' : ''}
        </span>
      </div>
      
      <div className="flex items-center space-x-2">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span className="text-gray-700 dark:text-gray-300">
          {stats.activeCollaborators} active now
        </span>
      </div>
      
      {stats.unreadComments > 0 && (
        <div className="flex items-center space-x-2">
          <ChatBubbleLeftIcon className="w-4 h-4 text-yellow-500" />
          <span className="text-gray-700 dark:text-gray-300">
            {stats.unreadComments} unread comment{stats.unreadComments !== 1 ? 's' : ''}
          </span>
        </div>
      )}
      
      {stats.pendingChanges > 0 && (
        <div className="flex items-center space-x-2">
          <PencilIcon className="w-4 h-4 text-orange-500" />
          <span className="text-gray-700 dark:text-gray-300">
            {stats.pendingChanges} pending change{stats.pendingChanges !== 1 ? 's' : ''}
          </span>
        </div>
      )}
      
      {stats.lastActivity && (
        <div className="flex items-center space-x-2 col-span-2">
          <ClockIcon className="w-4 h-4 text-gray-400" />
          <span className="text-gray-500 dark:text-gray-400 text-xs">
            Last activity: {stats.lastActivity.toLocaleTimeString()}
          </span>
        </div>
      )}
    </div>
  );
};

// Permission status component
interface PermissionStatusProps {
  user: CollaborationUser;
  compact?: boolean;
}

const PermissionStatus: React.FC<PermissionStatusProps> = ({ user, compact = false }) => {
  const getPermissionIcon = (role: CollaborationUser['role']) => {
    switch (role) {
      case 'owner':
        return <ShieldCheckIcon className="w-3 h-3 text-purple-500" />;
      case 'editor':
        return <PencilIcon className="w-3 h-3 text-lex-accent" />;
      case 'reviewer':
        return <EyeIcon className="w-3 h-3 text-yellow-500" />;
      case 'viewer':
        return <EyeIcon className="w-3 h-3 text-gray-400" />;
      default:
        return <EyeIcon className="w-3 h-3 text-gray-400" />;
    }
  };

  const getRoleColor = (role: CollaborationUser['role']) => {
    switch (role) {
      case 'owner':
        return 'text-purple-600 dark:text-purple-400';
      case 'editor':
        return 'text-lex-accent';
      case 'reviewer':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'viewer':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-1">
        {getPermissionIcon(user.role)}
        <span className={`text-xs capitalize ${getRoleColor(user.role)}`}>
          {user.role}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {getPermissionIcon(user.role)}
      <span className={`text-sm font-medium capitalize ${getRoleColor(user.role)}`}>
        {user.role}
      </span>
    </div>
  );
};

// Main CollaborationManager component
export const CollaborationManager: React.FC<CollaborationManagerProps> = ({
  documentId,
  versionId,
  isCurrentVersion,
  ownerPermissions,
  className = '',
  onManagePermissions,
  documentTitle,
  currentVersion,
  refreshTrigger,
  onDocumentUpdate,
  onVersionsRefresh,
}) => {
  const { keycloak } = useKeycloak();
  const { state, actions, status, isConnected } = useDocumentCollaboration(documentId);
  const { setOfflineMode } = useDocumentCheckoutStore();
  
  // Version-specific collaboration state
  const [versionCollaborators, setVersionCollaborators] = useState<any[]>([]);
  const [versionActivity, setVersionActivity] = useState({ active: 0, total: 0 });
  
  // WebSocket-based checkout functionality - scalable for hundreds of documents
  const {
    checkoutState,
    isLoading: isCheckoutLoading,
    error: checkoutError,
    checkout,
    checkin,
    forceCheckin,
    isCheckedOutByMe,
    isCheckedOutByOther,
    canCheckout,
    canCheckin,
    canForceCheckin
  } = useWebSocketCheckout({
    documentId,
    versionId, // Pass versionId for version-specific checkout
    onCheckoutChange: (state) => {
      console.log(`🔄 [CollaborationManager] Checkout state changed for ${versionId ? `version ${versionId}` : `document ${documentId}`}:`, state);
    },
    onError: (error) => {
      console.error(`❌ [CollaborationManager] Checkout error for ${versionId ? `version ${versionId}` : `document ${documentId}`}:`, error);
    }
  });

  // Derived state for UI
  const isCheckedOut = checkoutState.isLocked;
  const canPrepareOfflineCopy = Boolean(versionId) && !isCheckedOutByOther;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAllCollaborators, setShowAllCollaborators] = useState(false);
  const [expandedView, setExpandedView] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [collaborationEnabled, setCollaborationEnabled] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [collaboratorToRemove, setCollaboratorToRemove] = useState<CollaborationUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkoutDuration, setCheckoutDuration] = useState(24); // Default 24 hours
  const [showCheckoutOptions, setShowCheckoutOptions] = useState(false);
  
  // API-fetched collaborators state (same as CollaborationSettingsDrawer)
  const [apiCollaborators, setApiCollaborators] = useState<CollaborationUser[]>([]);
  const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
  const [collaboratorsError, setCollaboratorsError] = useState<string | null>(null);
  
  // Dynamic permission calculation based on actual collaboration data
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [dynamicOwnerPermissions, setDynamicOwnerPermissions] = useState(false);

  // Get current user ID from Keycloak token
  const getCurrentUserId = () => {
    if (keycloak.tokenParsed?.sub) {
      return keycloak.tokenParsed.sub;
    }
    return null;
  };

  // Calculate dynamic permissions based on collaboration data
  const calculateOwnerPermissions = (collaborators: CollaborationUser[]) => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      console.log('🔒 [CollaborationManager] No current user ID found');
      return false;
    }

    // Find current user in collaboration data
    const currentUserCollab = collaborators.find(collab => 
      collab.id === currentUserId || 
      collab.email === keycloak.tokenParsed?.email
    );

    if (!currentUserCollab) {
      console.log('🔒 [CollaborationManager] Current user not found in collaboration data');
      return false;
    }

    const isOwner = currentUserCollab.role === 'owner';
    console.log(`🔒 [CollaborationManager] Current user ${currentUserCollab.email} role: ${currentUserCollab.role}, isOwner: ${isOwner}`);
    
    setCurrentUserRole(currentUserCollab.role);
    return isOwner;
  };

  // API functions (same as CollaborationSettingsDrawer)
  const getAuthHeaders = (token?: string) => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  });

  const fetchCollaborationState = async (documentId: string, token?: string) => {
    const response = await fetch(`/api/v1/documents/${documentId}/collaboration`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch collaboration state: ${response.statusText}`);
    }
    
    return response.json();
  };

  // Load version-specific assigned collaborators (assignment model)
  const loadVersionCollaborators = async () => {
    if (!documentId || !versionId || !keycloak.token) return;
    
    // Prevent concurrent calls to the same function
    if (collaboratorsLoading) {
      console.log('🔄 [CollaborationManager] Skipping version collaborator load - already loading');
      return;
    }
    
    setCollaboratorsLoading(true);
    setCollaboratorsError(null);
    
    console.log(`🔍 [CollaborationManager] Loading assigned collaborators for version ${versionId} of document ${documentId}`);
    
    try {
      // Fetch version-specific collaboration state using assignment model
      const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/collaborators`, {
        method: 'GET',
        headers: getAuthHeaders(keycloak.token),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch version collaborators: ${response.statusText}`);
      }
      
      const versionResponse = await response.json();
      console.log('👥 [CollaborationManager] Version collaboration response:', versionResponse);

      // Process version-specific assigned collaborators
      if (versionResponse.success && versionResponse.data && Array.isArray(versionResponse.data)) {
        const collaboratorsData: CollaborationUser[] = versionResponse.data
          .filter((collab: any) => collab.status !== 'removed') // Filter out removed collaborators
          .map((collab: any) => ({
            id: collab.id,
            name: collab.name || collab.email || 'Unknown User', // Use name from backend, fallback to email
            email: collab.email,
            role: collab.role,
            status: collab.status,
            lastActive: collab.last_active ? new Date(collab.last_active) : new Date(),
            joinedAt: collab.joined_at ? new Date(collab.joined_at) : new Date(),
            permissions: collab.permissions || {},
            isAssigned: collab.isAssigned || true, // All returned collaborators are assigned
            isCurrentlyActive: collab.isCurrentlyActive || false, // Real-time presence status
            currentVersion: versionId, // Current version context
          }));
        
        console.log('👥 [CollaborationManager] Version API Response collaborators:', versionResponse.data);
        console.log('👥 [CollaborationManager] Filtered assigned collaborators (removed excluded):', collaboratorsData);
        setApiCollaborators(collaboratorsData);
        
        // Calculate dynamic owner permissions based on actual collaboration data
        const isOwner = calculateOwnerPermissions(collaboratorsData);
        setDynamicOwnerPermissions(isOwner);
        console.log(`🔒 [CollaborationManager] Dynamic owner permissions set to: ${isOwner}`);
      } else {
        console.log('👥 [CollaborationManager] No collaborators found in response');
        setApiCollaborators([]);
      }
    } catch (err) {
      console.error('❌ [CollaborationManager] Failed to load collaborators:', err);
      setCollaboratorsError('Failed to load collaborators. Please try again.');
      setApiCollaborators([]);
    } finally {
      setCollaboratorsLoading(false);
    }
  };

  // Load all document collaborators for assignment interface
  const loadDocumentCollaborators = async () => {
    if (!documentId || !keycloak.token) return;
    
    console.log(`🔍 [CollaborationManager] Loading all document collaborators for assignment interface`);
    
    try {
      const response = await fetch(`/api/v1/documents/${documentId}/collaborators/available`, {
        method: 'GET',
        headers: getAuthHeaders(keycloak.token),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch document collaborators: ${response.statusText}`);
      }
      
      const documentResponse = await response.json();
      console.log('👥 [CollaborationManager] Document collaborators response:', documentResponse);

      return documentResponse.success ? documentResponse.data : [];
    } catch (err) {
      console.error('❌ [CollaborationManager] Failed to load document collaborators:', err);
      return [];
    }
  };

  // Assign collaborator to version
  const assignCollaboratorToVersion = async (userId: string) => {
    if (!documentId || !versionId || !keycloak.token) return false;
    
    console.log(`🔗 [CollaborationManager] Assigning user ${userId} to version ${versionId}`);
    
    try {
      const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/assign/${userId}`, {
        method: 'POST',
        headers: getAuthHeaders(keycloak.token),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to assign collaborator: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('✅ [CollaborationManager] Assignment successful:', result);
      
      // Reload version collaborators to reflect the change
      await loadVersionCollaborators();
      
      return true;
    } catch (err) {
      console.error('❌ [CollaborationManager] Failed to assign collaborator:', err);
      return false;
    }
  };

  // Unassign collaborator from version
  const unassignCollaboratorFromVersion = async (userId: string) => {
    if (!documentId || !versionId || !keycloak.token) return false;
    
    console.log(`🔗 [CollaborationManager] Unassigning user ${userId} from version ${versionId}`);
    
    try {
      const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/assign/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(keycloak.token),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to unassign collaborator: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('✅ [CollaborationManager] Unassignment successful:', result);
      
      // Reload version collaborators to reflect the change
      await loadVersionCollaborators();
      
      return true;
    } catch (err) {
      console.error('❌ [CollaborationManager] Failed to unassign collaborator:', err);
      return false;
    }
  };

  // Version-specific WebSocket connection for real-time collaboration updates
  useEffect(() => {
    if (!documentId || !versionId || !keycloak.token) return;

    const connectVersionWebSocket = () => {
      try {
        const realmId = keycloak.tokenParsed?.iss?.split('/').pop();
        if (!realmId) return;

        // Connect to collaboration namespace
        const socket = io('/collaboration', {
          path: '/socket.io',
          auth: { token: keycloak.token },
          autoConnect: true,
        });

        socket.on('connect', () => {
          console.log(`🔗 [CollaborationManager] Connected to version collaboration WebSocket for ${versionId}`);
          
          // Join version-specific collaboration room
          socket.emit('join-document', { documentId });
        });

        // Listen for real-time collaboration events
        socket.on('user-joined', (event) => {
          console.log('👥 [CollaborationManager] User joined version:', event);
          loadVersionCollaborators(); // Refresh collaborator list
        });

        socket.on('user-left', (event) => {
          console.log('👋 [CollaborationManager] User left version:', event);
          loadVersionCollaborators(); // Refresh collaborator list
        });

        socket.on('user-typing', (event) => {
          console.log('⌨️ [CollaborationManager] User typing in version:', event);
          loadVersionCollaborators(); // Refresh to show typing status
        });

        socket.on('user-idle', (event) => {
          console.log('😴 [CollaborationManager] User idle in version:', event);
          loadVersionCollaborators(); // Refresh to show idle status
        });

        socket.on('disconnect', () => {
          console.log('❌ [CollaborationManager] Disconnected from version collaboration WebSocket');
        });

        return socket;
      } catch (error) {
        console.error('❌ [CollaborationManager] WebSocket connection error:', error);
      }
    };

    const socket = connectVersionWebSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [documentId, versionId, keycloak.token]);

  // Consolidated collaborator loading logic - prevents race conditions
  useEffect(() => {
    if (!documentId || !keycloak.token) return;
    
    setCollaborationEnabled(true);
    console.log(`[CollaborationManager] Managing collaboration for document ${documentId}, version ${versionId}`);
    
    // Join version-specific collaboration session
    if (versionId) {
      joinVersionCollaboration(documentId, versionId);
    }
    
    // Load version-specific assigned collaborators using assignment model
    console.log('🔄 [CollaborationManager] Loading assigned collaborators for version:', versionId);
    loadVersionCollaborators();

    // Cleanup function to leave version collaboration when component unmounts or version changes
    return () => {
      if (versionId) {
        leaveVersionCollaboration(documentId, versionId);
      }
    };
  }, [documentId, versionId, keycloak.token]);

  // Close checkout options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCheckoutOptions && event.target) {
        const target = event.target as Element;
        // Check if click is outside the dropdown and checkout button
        const dropdownElement = document.querySelector('.checkout-dropdown-container');
        const checkoutButton = document.querySelector('.checkout-button-container');
        
        if (dropdownElement && checkoutButton) {
          const isClickInsideDropdown = dropdownElement.contains(target);
          const isClickOnCheckoutButton = checkoutButton.contains(target);
          
          if (!isClickInsideDropdown && !isClickOnCheckoutButton) {
            console.log('🔍 [CollaborationManager] Click outside dropdown detected, closing');
            setShowCheckoutOptions(false);
          } else {
            console.log('🔍 [CollaborationManager] Click inside dropdown or button, keeping open');
          }
        } else {
          // Fallback: close dropdown if elements not found
          console.log('🔍 [CollaborationManager] Dropdown elements not found, closing');
          setShowCheckoutOptions(false);
        }
      }
    };

    if (showCheckoutOptions) {
      // Use a small delay to allow the dropdown to render
      setTimeout(() => {
        document.addEventListener('mouseup', handleClickOutside);
        console.log('🔍 [CollaborationManager] Dropdown opened, listening for outside clicks');
      }, 10);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [showCheckoutOptions]);

  // Initialize collaboration for this version
  const handleInitializeCollaboration = async () => {
    setIsInitializing(true);
    try {
      const response = await fetch(`/api/v1/documents/${documentId}/collaboration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collaboration_mode: 'collaborative',
          default_permissions: {
            canEdit: false,
            canComment: true,
            canTrackChanges: false,
            canAcceptReject: false,
            canShare: false,
            canManageUsers: false
          },
          allow_anonymous_comments: false,
          require_approval_for_changes: true,
          track_changes_enabled: true,
          comments_enabled: true
        })
      });

      if (response.ok) {
        setCollaborationEnabled(true);
        // Initialize the store
        await actions.initialize();
        console.log(`✅ [CollaborationManager] Collaboration initialized for document ${documentId}`);
      } else {
        const errorData = await response.json();
        console.error(`❌ [CollaborationManager] Failed to initialize collaboration:`, errorData);
        throw new Error(errorData.error || 'Failed to initialize collaboration');
      }
    } catch (error) {
      console.error(`❌ [CollaborationManager] Error initializing collaboration:`, error);
      // You could add a toast notification here
    } finally {
      setIsInitializing(false);
    }
  };

  // Use version-specific collaborators if available, fallback to API/store
  const activeCollaborators = versionCollaborators.length > 0 ? versionCollaborators : (apiCollaborators.length > 0 ? apiCollaborators : (state?.collaborators || []));
  
  // Update version activity stats based on actual collaborators
  React.useEffect(() => {
    const currentlyActive = activeCollaborators.filter(collab => collab.isCurrentlyActive).length;
    setVersionActivity({
      active: currentlyActive,
      total: activeCollaborators.length
    });
  }, [activeCollaborators]);
  
  // Use version-specific activity stats
  const collaborationStats = versionActivity.total > 0 ? {
    totalCollaborators: versionActivity.total,
    activeCollaborators: versionActivity.active,
    unreadComments: status?.unreadComments || 0,
    pendingChanges: status?.pendingChanges || 0,
  } : status;

  const handleInviteUser = async (invitation: any) => {
    try {
      console.log('🚀 [CollaborationManager] handleInviteUser called with:', invitation);
      console.log('🔑 [CollaborationManager] Keycloak token available:', !!keycloak.token);
      console.log('🔑 [CollaborationManager] Keycloak token length:', keycloak.token?.length);
      console.log('🔑 [CollaborationManager] Actions available:', !!actions.inviteUser);
      
      await actions.inviteUser(invitation, keycloak.token);
      console.log('✅ [CollaborationManager] Invitation sent successfully');
      
      setShowInviteModal(false);
    } catch (error) {
      console.error('❌ [CollaborationManager] Invitation failed:', error);
      // Error will be handled by the modal
      throw error;
    }
  };

  const handleUserClick = (user: CollaborationUser) => {
    if (onManagePermissions) {
      onManagePermissions(user.id);
    }
  };

  const handleShowAllCollaborators = () => {
    setShowAllCollaborators(true);
  };

  // Version-specific collaboration session management
  const joinVersionCollaboration = async (documentId: string, versionId: string) => {
    try {
      console.log(`🔗 [CollaborationManager] Joining version collaboration for ${versionId}`);
      
      const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/collaboration/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ [CollaborationManager] Joined version collaboration:`, result);
      } else {
        console.error(`❌ [CollaborationManager] Failed to join version collaboration:`, response.statusText);
      }
    } catch (error) {
      console.error(`❌ [CollaborationManager] Error joining version collaboration:`, error);
    }
  };

  const leaveVersionCollaboration = async (documentId: string, versionId: string) => {
    try {
      console.log(`🔌 [CollaborationManager] Leaving version collaboration for ${versionId}`);
      
      const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/collaboration/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ [CollaborationManager] Left version collaboration:`, result);
      } else {
        console.error(`❌ [CollaborationManager] Failed to leave version collaboration:`, response.statusText);
      }
    } catch (error) {
      console.error(`❌ [CollaborationManager] Error leaving version collaboration:`, error);
    }
  };


  // Checkout handlers
  const handleCheckout = async (duration?: number) => {
    try {
      setShowCheckoutOptions(false);
      const durationHours = duration || 24;
      console.log(`🔒 [CollaborationManager] Checking out document for ${durationHours} hours`);
      await checkout(durationHours);
    } catch (error) {
      console.error('❌ [CollaborationManager] Checkout failed:', error);
    }
  };

  const handleQuickCheckout = (hours: number, event?: React.MouseEvent) => {
    console.log(`🎯 [CollaborationManager] Quick checkout clicked: ${hours} hours`);
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    handleCheckout(hours);
  };

  const handleCheckin = async () => {
    try {
      await checkin({ 
        createNewVersion: false,
        versionComment: 'Manual check-in from collaboration panel'
      });
      setShowCheckinModal(false);
    } catch (error) {
      console.error('❌ [CollaborationManager] Checkin failed:', error);
    }
  };

  const handlePrepareOfflineCopy = () => {
    if (!versionId || isCheckedOutByOther) return;

    setOfflineMode(documentId, true);

    try {
      localStorage.setItem(
        `webelieve_offline_checkout_intent:${documentId}:${versionId}`,
        JSON.stringify({
          documentId,
          versionId,
          documentTitle,
          currentVersion,
          checkedOutExpiresAt: checkoutState.checkedOutExpiresAt,
          preparedAt: new Date().toISOString(),
        })
      );
    } catch (storageError) {
      console.warn('[CollaborationManager] Could not persist offline checkout intent:', storageError);
    }

    const url = `/documents/${encodeURIComponent(documentId)}/word-editor?version=${encodeURIComponent(versionId)}&launch=word&mode=offline`;
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      window.location.assign(url);
    }
  };

  const handleForceCheckin = async () => {
    try {
      await forceCheckin('Owner override - force checkin with unsaved changes');
      setShowCheckinModal(false);
    } catch (error) {
      console.error('❌ [CollaborationManager] Force checkin failed:', error);
    }
  };

  // Show collaboration setup if not enabled yet
  if (!collaborationEnabled && dynamicOwnerPermissions) {
    return (
      <div className={`collaboration-manager ${className}`}>
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <UserGroupIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Collaboration not enabled for this version
              </span>
            </div>
            <button
              onClick={handleInitializeCollaboration}
              disabled={isInitializing}
              className="px-3 py-1 bg-lex-accent text-lex-accent-contrast text-xs rounded-md hover:bg-lex-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
            >
              {isInitializing ? (
                <>
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Setting up...</span>
                </>
              ) : (
                <>
                  <UserPlusIcon className="w-3 h-3" />
                  <span>Enable Collaboration</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no collaborators and user has no permissions to invite
  if (activeCollaborators.length === 0 && !dynamicOwnerPermissions && collaborationEnabled) {
    return null;
  }

  return (
    <>
      <div className={`collaboration-manager ${className}`}>
        {/* Compact View (Default) */}
        {!expandedView ? (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              {/* Left: Collaborator Display */}
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="flex items-center">
                  <CogIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                
                {activeCollaborators.length > 0 ? (
                  <div className="flex items-center space-x-3">
                    {/* Avatar Stack */}
                    <UserAvatarGroup
                      users={activeCollaborators}
                      maxVisible={3}
                      size="sm"
                      showStatus={true}
                      clickable={dynamicOwnerPermissions}
                      onUserClick={handleUserClick}
                      onShowAll={handleShowAllCollaborators}
                    />
                    
                    {/* Active Status Indicator */}
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${
                        versionActivity.active > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                      }`}></div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {(() => {
                          const activeUsers = activeCollaborators.filter(user => user.isCurrentlyActive);
                          if (activeUsers.length === 0) return 'idle';
                          if (activeUsers.length === 1) {
                            const firstName = activeUsers[0].name?.split(' ')[0] || 'User';
                            return `${firstName} typing`;
                          }
                          return `${activeUsers.length} typing`;
                        })()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    No collaborators yet
                  </span>
                )}
              </div>
              
              {/* Right: Management Actions */}
              <div className="flex items-center space-x-2">
                {dynamicOwnerPermissions && (
                  <>
                    <button 
                      onClick={() => setShowSettingsDrawer(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                      title="Collaboration settings"
                    >
                      <CogIcon className="w-4 h-4" />
                    </button>
                    
                    {/* Document Control Actions */}
                    {!isCheckedOut ? (
                      <div className="relative checkout-button-container">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🔍 [CollaborationManager] Checkout button clicked, current state:', showCheckoutOptions);
                            setShowCheckoutOptions(!showCheckoutOptions);
                          }}
                          disabled={!canCheckout || isCheckoutLoading}
                          className={`px-3 py-1 text-white text-xs rounded-md transition-colors flex items-center space-x-1 ${
                            isCheckoutLoading 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : canCheckout 
                                ? 'bg-amber-600 hover:bg-amber-700' 
                                : 'bg-gray-400 cursor-not-allowed'
                          }`}
                          title="Checkout for exclusive editing"
                        >
                          {isCheckoutLoading ? (
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <LockClosedIcon className="w-3 h-3" />
                          )}
                          <span>{isCheckoutLoading ? 'Checking out...' : 'Checkout'}</span>
                          {!isCheckoutLoading && (
                            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </button>
                        
                        {/* Checkout Duration Dropdown */}
                        {showCheckoutOptions && (
                          <div 
                            className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999] min-w-48 checkout-dropdown-container"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseUp={(e) => e.stopPropagation()}
                          >
                            <div className="p-2" onClick={(e) => e.stopPropagation()}>
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Checkout Duration:</div>
                              <div className="space-y-1">
                                <button
                                  onClick={(e) => handleQuickCheckout(0.25, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>15 Minutes</span>
                                  <span className="text-gray-500">Quick lock</span>
                                </button>
                                <button
                                  onClick={(e) => handleQuickCheckout(0.5, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>30 Minutes</span>
                                  <span className="text-gray-500">Short edit</span>
                                </button>
                                <button
                                  onClick={(e) => handleQuickCheckout(1, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>1 Hour</span>
                                  <span className="text-gray-500">Quick edit</span>
                                </button>
                                <button
                                  onClick={(e) => handleQuickCheckout(4, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>4 Hours</span>
                                  <span className="text-gray-500">Half day</span>
                                </button>
                                <button
                                  onClick={(e) => handleQuickCheckout(8, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>8 Hours</span>
                                  <span className="text-gray-500">Full day</span>
                                </button>
                                <button
                                  onClick={(e) => handleQuickCheckout(24, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>24 Hours</span>
                                  <span className="text-gray-500">Default</span>
                                </button>
                                <button
                                  onClick={(e) => handleQuickCheckout(72, e)}
                                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                                >
                                  <span>3 Days</span>
                                  <span className="text-gray-500">Extended</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        {/* Checkout Status Display */}
                        <div className="flex items-center space-x-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded-md">
                          <LockClosedIcon className="w-3 h-3" />
                          <span>
                            {isCheckedOutByMe ? 'Checked out by you' : `Checked out by ${checkoutState.checkedOutBy?.name || 'another user'}`}
                          </span>
                        </div>
                        
                        {/* Checkout Timer */}
                        <CheckoutTimerBadge 
                          expiresAt={checkoutState.checkedOutExpiresAt}
                          isCheckedOut={checkoutState.isCheckedOut}
                        />
                        
                        {/* Checkin Button */}
                        {(canCheckin || canForceCheckin) && (
                          <button 
                            onClick={() => setShowCheckinModal(true)}
                            disabled={isCheckoutLoading}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                            title={canForceCheckin ? "Force checkin (Owner override)" : "Check in document"}
                          >
                            <LockOpenIcon className="w-3 h-3" />
                            <span>{canForceCheckin ? 'Force Checkin' : 'Checkin'}</span>
                          </button>
                        )}
                      </div>
                    )}
                    
                    <button 
                      onClick={handlePrepareOfflineCopy}
                      disabled={!canPrepareOfflineCopy || isCheckoutLoading}
                      className={`px-3 py-1 text-white text-xs rounded-md transition-colors flex items-center space-x-1 ${
                        canPrepareOfflineCopy && !isCheckoutLoading
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-gray-400 cursor-not-allowed'
                      }`}
                      title={isCheckedOutByOther ? 'This version is locked by another user' : 'Checkout, download, and stage an offline working copy'}
                    >
                      <ArrowDownTrayIcon className="w-3 h-3" />
                      <span>Offline Copy</span>
                    </button>
                  </>
                )}
                
                {!dynamicOwnerPermissions && activeCollaborators.length > 3 && (
                  <button 
                    onClick={() => setExpandedView(true)}
                    className="text-xs text-lex-accent hover:text-lex-accent-hover"
                  >
                    View all
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Expanded View */
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <UserGroupIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Document Collaboration
                </h4>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                  isConnected 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                  }`}></div>
                  <span>{isConnected ? 'Live' : 'Offline'}</span>
                </div>
              </div>
              
              <button 
                onClick={() => setExpandedView(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            {/* Statistics */}
            <CollaborationStatsDisplay stats={collaborationStats} />
            
            {/* Collaborators List */}
            {activeCollaborators.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active Collaborators
                </h5>
                <div className="space-y-2">
                  {activeCollaborators.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <UserAvatar
                          user={user}
                          size="sm"
                          showStatus={true}
                          showTooltip={false}
                          clickable={dynamicOwnerPermissions}
                          onClick={() => handleUserClick(user)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                        </div>
                        <PermissionStatus user={user} compact={true} />
                      </div>
                      
                      {dynamicOwnerPermissions && user.role !== 'owner' && (
                        <button
                          onClick={() => handleUserClick(user)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Manage permissions"
                        >
                          <CogIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Actions */}
            {ownerPermissions && (
              <div className="space-y-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                {/* Collaboration Actions */}
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => {
                      // TODO: Implement collaboration settings
                      console.log('Open collaboration settings');
                    }}
                    className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <CogIcon className="w-4 h-4" />
                    <span>Settings</span>
                  </button>
                </div>

                {/* Document Control Actions */}
                <div className="flex items-center space-x-2">
                  {!isCheckedOut ? (
                    <div className="relative checkout-button-container">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('🔍 [CollaborationManager] Expanded checkout button clicked, current state:', showCheckoutOptions);
                          setShowCheckoutOptions(!showCheckoutOptions);
                        }}
                        disabled={!canCheckout || isCheckoutLoading}
                        className={`flex items-center space-x-2 px-3 py-2 text-white text-sm rounded-md transition-colors ${
                          isCheckoutLoading 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : canCheckout 
                              ? 'bg-amber-600 hover:bg-amber-700' 
                              : 'bg-gray-400 cursor-not-allowed'
                        }`}
                        title="Checkout for exclusive editing"
                      >
                        {isCheckoutLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <LockClosedIcon className="w-4 h-4" />
                        )}
                        <span>{isCheckoutLoading ? 'Checking out...' : 'Checkout'}</span>
                        {!isCheckoutLoading && (
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                      
                      {/* Checkout Duration Dropdown */}
                      {showCheckoutOptions && (
                        <div 
                          className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[9999] min-w-48 checkout-dropdown-container"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onMouseUp={(e) => e.stopPropagation()}
                        >
                          <div className="p-3" onClick={(e) => e.stopPropagation()}>
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Checkout Duration:</div>
                            <div className="space-y-2">
                              <button
                                onClick={(e) => handleQuickCheckout(0.25, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>15 Minutes</span>
                                <span className="text-gray-500">Quick lock</span>
                              </button>
                              <button
                                onClick={(e) => handleQuickCheckout(0.5, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>30 Minutes</span>
                                <span className="text-gray-500">Short edit</span>
                              </button>
                              <button
                                onClick={(e) => handleQuickCheckout(1, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>1 Hour</span>
                                <span className="text-gray-500">Quick edit</span>
                              </button>
                              <button
                                onClick={(e) => handleQuickCheckout(4, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>4 Hours</span>
                                <span className="text-gray-500">Half day</span>
                              </button>
                              <button
                                onClick={(e) => handleQuickCheckout(8, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>8 Hours</span>
                                <span className="text-gray-500">Full day</span>
                              </button>
                              <button
                                onClick={(e) => handleQuickCheckout(24, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>24 Hours</span>
                                <span className="text-gray-500">Default</span>
                              </button>
                              <button
                                onClick={(e) => handleQuickCheckout(72, e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                              >
                                <span>3 Days</span>
                                <span className="text-gray-500">Extended</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-2">
                      {/* Checkout Status Display */}
                      <div className="flex items-center justify-between px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm rounded-md">
                        <div className="flex items-center space-x-2">
                          <LockClosedIcon className="w-4 h-4" />
                          <span>
                            {isCheckedOutByMe 
                              ? 'Checked out by you' 
                              : `Checked out by ${checkoutState.checkedOutBy?.name || 'another user'}`
                            }
                          </span>
                        </div>
                        
                        {/* Checkout Timer - Full version for expanded view */}
                        <CheckoutTimer 
                          expiresAt={checkoutState.checkedOutExpiresAt}
                          isCheckedOut={checkoutState.isCheckedOut}
                          compact={true}
                          showIcon={true}
                        />
                      </div>
                      
                      {/* Checkin Button */}
                      {(canCheckin || canForceCheckin) && (
                        <button 
                          onClick={() => setShowCheckinModal(true)}
                          disabled={isCheckoutLoading}
                          className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                          title={canForceCheckin ? "Force checkin (Owner override)" : "Check in document"}
                        >
                          <LockOpenIcon className="w-4 h-4" />
                          <span>{canForceCheckin ? 'Force Checkin' : 'Checkin'}</span>
                        </button>
                      )}
                    </div>
                  )}
                  
                  <button 
                    onClick={handlePrepareOfflineCopy}
                    disabled={!canPrepareOfflineCopy || isCheckoutLoading}
                    className={`flex items-center space-x-2 px-3 py-2 text-white text-sm rounded-md transition-colors ${
                      canPrepareOfflineCopy && !isCheckoutLoading
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                    title={isCheckedOutByOther ? 'This version is locked by another user' : 'Checkout, download, and stage an offline working copy'}
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    <span>Prepare Offline Copy</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Version Context Warning */}
            {!isCurrentVersion && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                <div className="flex items-center space-x-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm text-yellow-700 dark:text-yellow-300">
                    You're viewing a historical version. Collaboration features are limited.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteUser}
        documentId={documentId}
        documentTitle="Current Document" // TODO: Get actual document title
        currentUser={state?.currentUser || undefined}
        existingCollaborators={activeCollaborators}
      />

      {/* All Collaborators Modal */}
      {showAllCollaborators && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowAllCollaborators(false)}
            />
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  All Collaborators
                </h3>
                <button
                  onClick={() => setShowAllCollaborators(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-3">
                {activeCollaborators.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center space-x-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <UserAvatar
                      user={user}
                      size="md"
                      showStatus={true}
                      showTooltip={true}
                      clickable={dynamicOwnerPermissions}
                      onClick={() => {
                        handleUserClick(user);
                        setShowAllCollaborators(false);
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                      <PermissionStatus user={user} compact={true} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collaboration Settings Drawer - Version-Aware */}
      <CollaborationSettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        documentId={documentId}
        versionId={versionId} // Pass versionId to make it version-aware
        versionNumber={currentVersion} // Pass version number for display
        documentTitle={documentTitle} // Pass document title for display
        collaborators={activeCollaborators}
        currentUser={state.currentUser || undefined}
        isOwner={dynamicOwnerPermissions}
        onInviteUser={() => {
          setShowSettingsDrawer(false);
          setShowInviteModal(true);
        }}
        onUpdateRole={(userId, newRole) => {
          // TODO: Implement role update
          console.log('Update role for user:', userId, 'to:', newRole);
        }}
        onRemoveUser={(collaborator) => {
          if (collaborator) {
            setCollaboratorToRemove(collaborator);
            setShowRemoveModal(true);
          }
        }}
        onUpdatePermissions={(userId, permissions) => {
          // TODO: Implement permission update
          console.log('Update permissions for user:', userId, 'permissions:', permissions);
        }}
        onAssignToVersion={assignCollaboratorToVersion}
        onUnassignFromVersion={unassignCollaboratorFromVersion}
      />

      {/* Remove Collaborator Modal */}
      <RemoveCollaboratorModal
        isOpen={showRemoveModal}
        collaborator={collaboratorToRemove}
        isProcessing={isRemoving}
        onConfirm={async () => {
          if (!collaboratorToRemove) return;
          
          setIsRemoving(true);
          try {
            console.log('🗑️ Attempting to remove user:', {
              id: collaboratorToRemove.id,
              name: collaboratorToRemove.name,
              email: collaboratorToRemove.email,
              role: collaboratorToRemove.role,
              permissions: collaboratorToRemove.permissions
            });
            
            // Call the API to remove the collaborator
            const response = await fetch(`/api/v1/documents/${documentId}/collaborators/${collaboratorToRemove.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${keycloak.token}`,
                'Content-Type': 'application/json',
              },
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              
              console.error('❌ API Error Details:', {
                status: response.status,
                statusText: response.statusText,
                errorData: errorData,
                url: `/api/v1/documents/${documentId}/collaborators/${collaboratorToRemove.id}`
              });
              
              // Handle permission errors specifically
              if (errorData.error?.includes('does not have permission to remove collaborators') ||
                  errorData.message?.includes('does not have permission to remove collaborators')) {
                console.log('❌ Permission denied: User cannot remove collaborators');
                // Close modals
                setShowRemoveModal(false);
                setCollaboratorToRemove(null);
                alert(`You don't have permission to remove collaborators from this document. Only the document owner can add or remove collaborators.`);
                return;
              }
              
              // Handle case where user is already removed or doesn't exist
              if (response.status === 404 || response.status === 403 ||
                  errorData.message?.includes('not found') || 
                  errorData.message?.includes('already removed') ||
                  errorData.message?.includes('does not have access') ||
                  errorData.message?.includes('is not an active collaborator')) {
                console.log('✅ User was already removed or doesn\'t exist, refreshing collaboration state');
                // Close modals and refresh state
                setShowRemoveModal(false);
                setShowSettingsDrawer(false);
                setCollaboratorToRemove(null);
                await actions.initialize();
                alert(`${collaboratorToRemove.name || collaboratorToRemove.email} is no longer a collaborator on this document.`);
                return;
              }
              
              throw new Error(errorData.message || `Failed to remove collaborator: ${response.statusText} (${response.status})`);
            }

            const result = await response.json();
            console.log('✅ User removed successfully:', result);

            // Close modals
            setShowRemoveModal(false);
            setShowSettingsDrawer(false);
            setCollaboratorToRemove(null);
            
            // Show success message (could be replaced with a toast notification)
            alert(`${collaboratorToRemove.name || collaboratorToRemove.email} has been removed from this document.`);
            
            // Refresh collaboration data instead of full page reload
            await actions.initialize();
            
          } catch (error) {
            console.error('❌ Failed to remove user:', error);
            alert(`Failed to remove ${collaboratorToRemove.name || collaboratorToRemove.email}. ${error instanceof Error ? error.message : 'Please try again.'}`);
          } finally {
            setIsRemoving(false);
          }
        }}
        onCancel={() => {
          setShowRemoveModal(false);
          setCollaboratorToRemove(null);
        }}
      />

      {/* Checkin Modal */}
      <CheckinModal
        isOpen={showCheckinModal}
        onClose={() => setShowCheckinModal(false)}
        documentId={documentId}
        documentTitle={documentTitle || `Document ${documentId}`}
        currentVersion={currentVersion}
        hasWorkingCopy={checkoutState.checkedOutBy !== null}
        lastLocalSaveAt={checkoutState.checkedOutAt}
        versionId={versionId} // Pass versionId for version-specific checkin
        onCheckinSuccess={() => {
          console.log('✅ [CollaborationManager] Checkin completed successfully');
          // Refresh the document data to show the new version
          if (onDocumentUpdate) {
            console.log('🔄 [CollaborationManager] Triggering document update');
            onDocumentUpdate();
          }
          // Refresh the versions list to show the new version
          if (onVersionsRefresh) {
            console.log('🔄 [CollaborationManager] Triggering versions refresh');
            onVersionsRefresh();
          }
          // Reload version collaborators to reflect updated state
          loadVersionCollaborators();
        }}
      />
    </>
  );
};

export default CollaborationManager; 
